import { listOfMonths } from "../constants.ts";
import {
  BudgetBodyInfo,
  BudgetData,
  BudgetDataItem,
  BudgetInsertIds,
  CreateBudgetItems,
} from "../types";
import { getDateInfo, getFrequencyValue } from "./helper.ts";

export const getMonthlyTotalAmount = (
  budget: BudgetData[],
  month: string,
  year: number,
  type: string,
) => {
  let amount = 0;

  if (!budget.length) {
    return amount;
  }

  budget.forEach((item: BudgetData) => {
    if (item.month.toLowerCase() === month && item.year === year) {
      item[type].forEach((data: BudgetDataItem) => {
        amount += data.value;
      });
    }
  });

  return amount;
};

export const createInitialBudget = (
  budget: BudgetBodyInfo[],
  insertIds: BudgetInsertIds[],
) => {
  if (!budget.length) {
    return [];
  }

  const initialBudget: BudgetData[] = [];
  const { currentYear } = getDateInfo();
  const count = listOfMonths.length - 1;

  for (let i = 0; i <= count; i++) {
    const income: BudgetDataItem[] = [];
    const expense: BudgetDataItem[] = [];

    budget.forEach((item: BudgetBodyInfo, index: number) => {
      if (listOfMonths[i] === item.month) {
        const ids = insertIds[index];
        const entry = {
          label: item.label,
          value: item.amount,
          paid: item.paid,
          budget_id: ids?.budget_id ?? null,
          budget_date_id: ids?.budget_date_id ?? null,
        };

        if (item.type === "income") {
          income.push(entry);
        }
        if (item.type === "expense") {
          expense.push(entry);
        }
      }
    });

    initialBudget.push({
      year: currentYear,
      month: listOfMonths[i],
      income,
      expense,
    });
  }

  return initialBudget;
};

export const formatBudgetData = (
  income: BudgetDataItem[],
  expense: BudgetDataItem[],
) => {
  if (!income.length || !expense.length) {
    return [];
  }

  const formattedBudget: BudgetBodyInfo[] = [];
  const { currentYear } = getDateInfo();
  const count = listOfMonths.length - 1;

  for (let i = 0; i <= count; i++) {
    income.forEach((response: BudgetDataItem) => {
      formattedBudget.push({
        label: response.label,
        type: "income",
        amount: response.value,
        paid: response.paid,
        month: listOfMonths[i],
        year: currentYear,
        frequency: response.frequency,
      });
    });

    expense.forEach((response: BudgetDataItem) => {
      formattedBudget.push({
        label: response.label,
        type: "expense",
        amount: response.value,
        paid: response.paid,
        month: listOfMonths[i],
        year: currentYear,
        category_id: response.category_id,
        frequency: response.frequency,
      });
    });
  }

  return formattedBudget;
};

export const addNewBudgetItem = (
  clonedBudget: BudgetData[],
  month: string,
  year: number,
  type: string,
) => {
  clonedBudget.forEach((item: BudgetData, i: number) => {
    if (month === item.month.toLowerCase() && year === item.year) {
      const currentItems: BudgetDataItem[] = [...item[type]];

      currentItems.push({
        label: "",
        value: 0,
        paid: false,
        frequency: "Monthly",
        cadence: "Current Month",
        budget_id: null,
        budget_date_id: currentItems[0].budget_date_id,
        temp: true,
      });

      item[type] = currentItems;
    }
  });

  return clonedBudget;
};

export const formatBudgetItem = (data: Object, month: string, year: number) => {
  const budgetEntries: BudgetDataItem[] = [];

  Object.values(data).forEach((item) => {
    const {
      value,
      checked,
      frequency,
      label,
      cadence,
      category_id,
    }: CreateBudgetItems = item;

    const freqVal = getFrequencyValue(Number(value), month, year, frequency);

    budgetEntries.push({
      label,
      value: freqVal,
      paid: checked,
      frequency,
      cadence,
      category_id,
      budget_id: null,
      budget_date_id: null,
      temp: false,
    });
  });

  return budgetEntries;
};

export const reformatBudgetItem = (
  updatedItem: Object,
  budgetId: number | null,
  budgetDateId: number | null,
  month: string,
  year: number,
  isPaid?: boolean,
  frequency?: string,
  cadence?: string,
  category_id?: number,
) => {
  const refactoredItem: BudgetDataItem[] = Object.entries(updatedItem).map(
    (item) => {
      const freqVal = getFrequencyValue(item[1], month, year, frequency);

      return {
        label: item[0],
        value: freqVal,
        paid: isPaid || false,
        frequency: frequency || "Monthly",
        cadence,
        category_id,
        budget_id: budgetId,
        budget_date_id: budgetDateId,
        temp: false,
      };
    },
  );

  return refactoredItem;
};

export const sortBudget = (
  a: BudgetDataItem,
  b: BudgetDataItem,
  sort?: string,
) => {
  if (sort === "Low - High") {
    return a.label === ""
      ? 1
      : b.label === ""
        ? -1
        : a.value > b.value
          ? 1
          : a.value < b.value
            ? -1
            : 0;
  }

  if (sort === "High - Low") {
    return a.label === ""
      ? 1
      : b.label === ""
        ? -1
        : a.value < b.value
          ? 1
          : a.value > b.value
            ? -1
            : 0;
  }

  if (sort === "Paid") {
    const paidA = a.paid || false;
    const paidB = b.paid || false;

    return a.label === ""
      ? 1
      : b.label === ""
        ? -1
        : paidA < paidB
          ? 1
          : paidA > paidB
            ? -1
            : 0;
  }

  if (sort === "Unpaid") {
    const paidA = a.paid || false;
    const paidB = b.paid || false;

    return a.label === ""
      ? 1
      : b.label === ""
        ? -1
        : paidA > paidB
          ? 1
          : paidA < paidB
            ? -1
            : 0;
  }

  if (sort === "Z - A") {
    return a.label === ""
      ? 1
      : b.label === ""
        ? -1
        : a.label.toLowerCase() < b.label.toLowerCase()
          ? 1
          : a.label.toLowerCase() > b.label.toLowerCase()
            ? -1
            : 0;
  }

  return a.label === ""
    ? 1
    : b.label === ""
      ? -1
      : a.label.toLowerCase() > b.label.toLowerCase()
        ? 1
        : a.label.toLowerCase() < b.label.toLowerCase()
          ? -1
          : 0;
};

export const getMonthlyPaidExpenses = (
  budget: BudgetData[],
  month: string,
  year: number,
) => {
  let amount = 0;

  if (!budget.length) {
    return amount;
  }

  budget.forEach((item: BudgetData) => {
    if (item.month.toLowerCase() === month && item.year === year) {
      item.expense.forEach((data: BudgetDataItem) => {
        if (data.paid) {
          amount += data.value;
        }
      });
    }
  });

  return amount;
};
